import { createRef, memo, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CustomTextField } from '../../Layout/CustomTextField';
import validator from "validator";
import { CircularProgress, MenuItem } from "@mui/material";
import { uploadMedia } from "../../../services/shared";
import { createHubspotTicket, getCategories } from "../../../utilities/Response";
import { updateEnvironment } from "../../../actions/environment.action";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';

const Create = (props: { backMethod?: any }) => {

  const dispatch = useDispatch();
  const hiddenFileInput: any = useRef(null);
  const [error, setError] = useState<boolean>(false);
  const [attachment, setAttachment] = useState<File[]>([]);
  const design = useSelector((state: any) => state?.design);
  const [categories, setCategories] = useState<Category[]>([]);
  const [apiInProgress, setAPIInProgress] = useState<boolean>(false);
  const [postMessageRecieved, setPostMessageRecieved] = useState(false);
  const environment = useSelector((state: any) => state?.environment);

  const [ticket, setTicket] = useState<Ticket>({
    attachments: [],
    content: '',
    email: '',
    name: '',
    subject: '',
    category: ''
  });


  useEffect(() => {

    const messageHandler = (event: any) => {
      if (event.data.origin === 'BotPenguin') {
        if (event.data.action === 'getRequestedUserMeta' && !ticket.email) {
          const { name, email } = event.data.payload;
          setTicket(previousValue => ({ ...previousValue, name, email }));
          setPostMessageRecieved(true);
        }
      }
    }
    window.addEventListener('message', messageHandler);

    if (!ticket.email && !postMessageRecieved) {

      window.parent.postMessage({
        origin: 'BotPenguin',
        action: 'requestingUserMeta'
      }, '*');

      // Only for dev purpose
      // if (process.env.NODE_ENV === 'development') {
      //   window.postMessage({
      //     origin: 'BotPenguin',
      //     action: 'getRequestedUserMeta',
      //     payload: { name: 'BP TEST', email: 'divyansh@relinns.com' }
      //   }, '*');
      // }
    }


    return () => {
      window.removeEventListener('message', messageHandler);
    };
  }, [ticket.email, postMessageRecieved]);


  useEffect(() => {
    getCategories()
      .then((categories: any) => {
        setCategories(categories);
      })
      .catch((err) => console.log('Can\'t fetch categories' + err));
  }, [])

  const handleInputChange = (value: string, key: string) => {
    setTicket(previousValue => ({ ...previousValue, [key]: value }));
  }

  const emailValidator = (email: string): boolean => {
    return !email || validator.isEmail(email.trim());
  }

  const handleClick = () => {
    hiddenFileInput.current.value = null;
    hiddenFileInput.current.click();
  };

  const uploadData = (e: any) => {
    setAttachment((a) => [...a, { name: e.target.files[0].name, url: '', uploaded: false, failed: false }]);

    const form = new FormData();
    form.append('file', e.target.files[0], e.target.files[0].name);
    uploadMedia(form)
      .then((data: any) => {
        if (!data.data) {
          throw new Error('Upload failed');
        }
        setAttachment(a => {
          return [
            ...a.filter(a => a.name !== e.target.files[0].name),
            { name: e.target.files[0].name, url: data.data, uploaded: true, failed: false }]
        });
      })
      .catch((err: any) => {

        setAttachment(a => {
          return [
            ...a.filter(a => a.name !== e.target.files[0].name),
            { name: e.target.files[0].name, url: '', uploaded: true, failed: true }]
        });
      })
      .finally(() => scrollBottom());
  }

  const deleteFile = (file: File) => {
    setAttachment(attachment => attachment.filter(a => a.name !== file.name));
  }

  const submitTicket = () => {
    if (!isTicketValid() || apiInProgress) {
      return false;
    }

    setAPIInProgress(true);

    createHubspotTicket({ ...ticket, attachments: attachment.map(a => a.url) })
      .then(() => {

        dispatch(updateEnvironment({ ticketsEmail: ticket.email }));
        props.backMethod();
      })
      .catch((e) => {
        console.log(e);
        setError(true);

        setTimeout(() => {
          setError(false);
        }, 3000);
      })
      .finally(() => setAPIInProgress(false));
  }

  const isTicketValid = () => {
    return validator.isEmail(ticket.email.trim()) && ticket.name.trim() && ticket.subject.trim() && ticket.content.trim() &&
      ((!!categories.length && ticket.category.trim()) || !categories.length);
  }

  const scrollBottom = () => {
    try {
      if ((window as any).newNoteRef && (window as any).newNoteRef.current) {
        (window as any).newNoteRef.current.scrollTo({
          top: (window as any).newNoteRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    } catch (e) {
      console.log(e);
    }
  }

  useEffect(() => {
    (window as any).newNoteRef = createRef();
  }, []);


  useEffect(() => {
    setTicket(previousValue => ({ ...previousValue, email: environment.ticketsEmail || '' }));
  }, [environment.ticketsEmail]);

  return (
    <>
      <div id='ele-143' className="relative h-full overflow-y-auto">
        <div id='ele-144' className="flex flex-col h-full pt-4 justify-between">

          <div id='ele-145' className='w-full px-2 pb-3 h-[calc(100%-3rem)] overflow-scroll hide-scrollbar' ref={(window as any).newNoteRef}>

            <div id='ele-146' className="text-primaryText text-base font-mSemiBold">Ticket Details</div>

            <div id='ele-147' className='mt-4'>
              <CustomTextField design={design}
                required
                size="small"
                label='Full Name'
                value={ticket.name}
                inputProps={{ maxLength: 50 }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e.target.value, 'name')}
              />
            </div>
            <div id='ele-148' className='mt-4'>
              <CustomTextField design={design}
                required
                size="small"
                label='Email'
                value={ticket.email}
                inputProps={{ maxLength: 100 }}
                error={!emailValidator(ticket.email)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e.target.value, 'email')}
              />
            </div>

            <div id='ele-149' className='mt-4'>
              <CustomTextField design={design}
                required
                size="small"
                label='Issue Title'
                value={ticket.subject}
                inputProps={{ maxLength: 500 }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e.target.value, 'subject')}
              />
            </div>

            <div id='ele-150' className='mt-4'>
              <CustomTextField design={design}
                required
                size="small"
                select
                label='Category'
                value={ticket.category}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e.target.value, 'category')}
              >
                {
                  categories.map(category => {
                    return <MenuItem className='ele-tickets-category-menuitem' key={'category ' + category.label} value={category.value}>
                      {category.label}
                    </MenuItem>
                  })
                }
              </CustomTextField>
            </div>

            <div id='ele-152' className='mt-4'>
              <CustomTextField design={design}
                required
                label='Issue Description' multiline rows={3}
                value={ticket.content}
                inputProps={{ maxLength: 1200 }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e.target.value, 'content')}
              />
            </div>

            {
              !!attachment.length &&
              <div id='ele-153' className='mt-4 flex flex-col'>
                <div id='ele-154' className="text-primaryText text-base font-mSemiBold">Attachment</div>
                {
                  attachment.map(file => {
                    return <div className="ele-create-ticket-attachment-container w-full h-12 flex justify-between bg-secondaryBgColor items-center px-4 mt-3 rounded-md" key={'file-' + file.name}>
                      <div className="ele-create-ticket-attachment-name align-middle items-center w-4/6 truncate ">{file.name} </div>
                      {file.uploaded
                        ? <div>
                          {file.failed && <span className="ele-create-ticket-attachment-upload-failed text-bpRed mx-2">Failed</span>}                          
                          <CloseIcon className="align-middle !font-bold !text-2xl text-bpRed2 cursor-pointer ele-create-ticket-attachment-close-icon" onClick={() => deleteFile(file)} />
                        </div>
                        : <CircularProgress size={16} />
                      }

                    </div>
                  })
                }
              </div>
            }

          </div>

          <div id='ele-159' className="flex justify-between border-y border-solid border-gray-300 bg-white w-full fixed bottom-24 left-0 px-4 py-2">
            {error && <div id='ele-160' className="fixed bottom-40 left-0 px-4 py-2 text-center w-full text-bpRed bg-white border-t border-solid border-gray-300 rounded-t-lg">Uh-oh, there was a problem, please try again.</div>}
            <div id='ele-161' className="flex align-middle">
              <input id='ele-162' className="hidden" ref={hiddenFileInput} type="file" accept="image/*" onChange={(e) => uploadData(e)}></input>
              <AttachFileIcon className="m-auto !font-bold !text-2xl text-footer cursor-pointer" onClick={() => handleClick()} />
            </div>
            <div className="flex" id='ele-163'>

              <div
                id='ele-164'
                className="align-middle justify-center items-center text-black rounded-lg px-6 py-2 cursor-pointer h-10"
                style={{ backgroundColor: '#DFE1E4' }}
                onClick={props.backMethod}
              >
                Cancel
              </div>

              <div
                id='ele-165'
                className={`align-middle justify-center items-center text-white rounded-lg px-6 py-2 ml-4 h-10 ${!isTicketValid() ? 'opacity-50 cursor-default' : 'cursor-pointer'}`}
                style={{ backgroundColor: design?.themeColor }}
                onClick={submitTicket}
              >
                Submit Ticket
                <span id='ele-166' className="ml-2 ">{apiInProgress && <CircularProgress size={12} sx={{ color: 'white' }} />}</span>
              </div>

            </div>
          </div>

        </div>
        {/* <div className="h-12 w-full absolute border"></div> */}
      </div >
    </>
  );
}

export default memo(Create);

interface Ticket {
  email: string;
  name: string;
  attachments: string[];
  subject: string;
  content: string;
  category: string;
}

type File = { name: string, url: string, uploaded: boolean, failed: boolean };
type Category = { label: string, value: string };